/**
 * 日ビュー（デイタイムライン）
 *
 * 1日分のデータを「予定」「TODO」「アクティビティ」の3列で表示する
 * カスタム実装（FullCalendar Premium 不要）
 */

import { format, parseISO } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getReadHistoryList } from '@/api/services/progress'
import type { TodoResponse } from '@/api/types/todo'
import type { ReadHistory } from '@/api/types/user'
import { getMotionConfig } from '@/lib/activity-format-utils'
import { ensureUtc, isSameDate } from '@/lib/date-utils'
import { listSleepEpisodes } from '@/lib/garmin-sleep'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'
import { useOverlandStore } from '@/store/useOverlandStore'
import { useSleepStore } from '@/store/useSleepStore'
import { useTodoStore } from '@/store/useTodoStore'

import { CalendarToolbar, type CalendarViewMode } from './CalendarToolbar'
import { formatCrossDayRange, isCrossDay } from './cross-day-label'
import { blockHorizontalStyle, columnInnerWidth, computeOverlapLayout } from './overlap-layout'
import { isPointerInside, PARKED_TODO_MIME } from './parking-tray-utils'
import { ParkingTray } from './ParkingTray'
import { rescheduleTodo } from './schedule-actions'
import { buildScheduleItems, findItemTodo, isDeadlineTodo, SCHEDULE_COLORS, type ScheduleItem } from './schedule-items'
import { scrollToNow } from './scroll-to-now'
import { SlotCreateDialog } from './SlotCreateDialog'
import { buildTodayOverlay, findOverlappingSlot, hasSlotLane } from './today-overlay'
import { SLOT_LANE_WIDTH, SLOT_STYLE } from './today-slot-style'
import { SlotBadge, TodayAdviceRow, TodaySuggestionLayer } from './TodaySuggestionLayer'
import type { ParkedTodosState } from './useParkedTodos'
import { useScheduleSources } from './useScheduleSources'
import { useSlotCreate } from './useSlotCreate'

const HOUR_HEIGHT = 60 // px per hour
const TOTAL_HOURS = 24
const COLUMN_LABELS = ['予定', 'TODO', 'アクティビティ']

const fmtTime = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
const fmtDate = (d: Date) => fmtTime(d.getHours() * 60 + d.getMinutes())

type TimeBlock = {
  id: string
  title: string
  subtitle?: string
  start: Date
  end: Date
  /** 表示日に切り詰める前の開始・終了 */
  realStart?: Date
  realEnd?: Date
  color: string
  column: number // 0=予定, 1=TODO, 2=アクティビティ
  icon?: string
  todoId?: number
  /** ドラッグ・リサイズで時刻を変更できるか */
  editable?: boolean
}

const columnOf = (item: ScheduleItem) => {
  if (item.kind === 'todo' || item.kind === 'workout') return 1
  if (item.kind === 'done') return item.isEvent ? 0 : 1
  return 0
}

type Props = {
  onViewChange?: (view: CalendarViewMode) => void
  initialDate?: Date
  onDateChange?: (date: Date) => void
  onAutoSchedule?: () => void
  isScheduling?: boolean
  onTodoClick?: (todo: TodoResponse) => void
  parked: ParkedTodosState
}

export const DayTimelinePane = ({
  onViewChange,
  initialDate,
  onDateChange,
  onAutoSchedule,
  isScheduling,
  onTodoClick,
  parked,
}: Props) => {
  const [selectedDate, setSelectedDate] = useState(() => initialDate ?? new Date())
  const { openSlot, dialogProps: slotDialogProps } = useSlotCreate()
  const trayRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  // ドラッグ選択用の状態
  const dragRef = useRef<{ startY: number; column: number } | null>(null)
  const [dragPreview, setDragPreview] = useState<{ top: number; height: number; column: number } | null>(null)

  // ブロック移動DnD用の状態
  const blockDragRef = useRef<{
    block: TimeBlock
    startY: number
    originalTop: number
    containerHeight: number
  } | null>(null)
  const [blockDragState, setBlockDragState] = useState<{ blockId: string; offsetTop: number } | null>(null)
  const justDraggedRef = useRef(false)

  // ブロックリサイズ用の状態
  const resizeDragRef = useRef<{
    block: TimeBlock
    edge: 'top' | 'bottom'
    startY: number
    containerHeight: number
    originalStartMin: number
    originalEndMin: number
  } | null>(null)
  const [resizeState, setResizeState] = useState<{
    blockId: string
    deltaStartMin: number
    deltaEndMin: number
  } | null>(null)

  // Stores
  const todos = useTodoStore((s) => s.todos)
  const hasFullAccess = useAppStore((s) => s.hasFullAccess)
  const { activities, fetchActivities, sleep, fetchSleep } = useGarminStore()
  const { visits, movementSessions: storeMovementSessions, fetchVisits, fetchLocations } = useOverlandStore()

  const { calendarEvents, workoutSessions, sleepWindows, load: loadSources } = useScheduleSources()
  const [readHistory, setReadHistory] = useState<ReadHistory[]>([])

  // データ取得
  const fetchData = useCallback(async () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    const dayStart = new Date(selectedDate)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(selectedDate)
    dayEnd.setHours(23, 59, 59, 999)

    void loadSources({ start: dayStart.toISOString(), end: dayEnd.toISOString() }, { withSleepPlan: hasFullAccess })

    try {
      const res = await getReadHistoryList(100, dateStr)
      setReadHistory(res.items)
    } catch {
      // ignore
    }

    if (hasFullAccess) {
      void fetchVisits(dateStr).then(() => fetchLocations(dateStr))
      fetchSleep(dateStr)
    }
  }, [selectedDate, hasFullAccess, fetchVisits, fetchLocations, fetchSleep, loadSources])

  useEffect(() => {
    void fetchData()
  }, [fetchData])

  useEffect(() => {
    if (hasFullAccess) fetchActivities(30)
  }, [hasFullAccess, fetchActivities])

  // 日付ナビゲーション
  const updateDate = useCallback(
    (d: Date) => {
      setSelectedDate(d)
      onDateChange?.(d)
    },
    [onDateChange],
  )
  const goToday = () => updateDate(new Date())
  const goPrev = () => {
    setSelectedDate((d) => {
      const next = new Date(d.getTime() - 86400000)
      onDateChange?.(next)
      return next
    })
  }
  const goNext = () => {
    setSelectedDate((d) => {
      const next = new Date(d.getTime() + 86400000)
      onDateChange?.(next)
      return next
    })
  }

  const isToday = isSameDate(selectedDate, new Date())

  // 今日の過ごし方（睡眠レポートの提案と時間帯ごとの調子）。無い・古いときは store 側で作り直す
  const today = useSleepStore((s) => s.today)
  const isTodayGenerating = useSleepStore((s) => s.isTodayGenerating)
  const fetchToday = useSleepStore((s) => s.fetchToday)
  useEffect(() => {
    if (hasFullAccess && isToday) void fetchToday()
  }, [hasFullAccess, isToday, fetchToday])
  const todayOverlay = useMemo(
    () =>
      hasFullAccess ? buildTodayOverlay({ today, date: selectedDate, todos, hiddenTodoIds: parked.parkedIds }) : null,
    [hasFullAccess, today, selectedDate, todos, parked.parkedIds],
  )
  // TODO 列の右端に集中・運動の帯を出すときは、TODO 列のタスクをその分細くして帯を隠さない
  const slotLaneWidth = hasSlotLane(todayOverlay) ? SLOT_LANE_WIDTH : 0

  // TimeBlock 生成
  const blocks = useMemo((): TimeBlock[] => {
    const dayStart = new Date(selectedDate)
    dayStart.setHours(0, 0, 0, 0)
    const dayEnd = new Date(selectedDate)
    dayEnd.setHours(23, 59, 59, 999)

    const clamp = (d: Date) => (d < dayStart ? dayStart : d > dayEnd ? dayEnd : d)

    // Column 0/1: 週ビューと共通の予定（Google カレンダー・TODO・ローカル予定・完了・筋トレ・睡眠プラン）
    const result: TimeBlock[] = buildScheduleItems({
      todos,
      calendarEvents,
      workoutSessions,
      sleepWindows,
      hiddenTodoIds: parked.parkedIds,
    })
      .filter((item) => !item.allDay && item.start <= dayEnd && item.end >= dayStart)
      .map((item) => ({
        id: item.id,
        title: item.title,
        subtitle: item.subtitle,
        start: item.start,
        end: item.end,
        color: SCHEDULE_COLORS[item.kind],
        column: columnOf(item),
        icon: item.icon || undefined,
        todoId: item.todoId,
        editable: item.editable,
      }))

    // Column 2: 訪問
    visits
      .filter((v) => v.arrival_date)
      .forEach((v) => {
        const arrival = parseISO(v.arrival_date!)
        const departure = v.departure_date ? parseISO(v.departure_date) : new Date()
        if (arrival > dayEnd || departure < dayStart) return
        const placeName = v.place?.display_name ?? v.place?.auto_name ?? '不明な場所'
        result.push({
          id: `visit-${v.id}`,
          title: placeName,
          subtitle: !v.departure_date ? '滞在中' : undefined,
          start: arrival,
          end: departure,
          color: '#6366f1',
          column: 2,
          icon: '📍',
        })
      })

    // Column 2: 移動セッション
    storeMovementSessions.forEach((s) => {
      const start = parseISO(s.startTime)
      const end = parseISO(s.endTime)
      if (start > dayEnd || end < dayStart) return
      const icon = getMotionConfig(s.motion).emoji
      result.push({
        id: s.id,
        title: `移動 ${s.estimatedDistanceKm.toFixed(1)}km`,
        start,
        end,
        color: '#f59e0b',
        column: 2,
        icon,
      })
    })

    // Column 2: 睡眠
    listSleepEpisodes(sleep).forEach((episode) => {
      const dto = episode.dailySleepDTO
      if (!dto?.sleepStartTimestampGMT || !dto.sleepEndTimestampGMT) return
      const sleepStart = new Date(dto.sleepStartTimestampGMT)
      const sleepEnd = new Date(dto.sleepEndTimestampGMT)
      if (!isSameDate(sleepStart, selectedDate) && !isSameDate(sleepEnd, selectedDate)) return
      const durationHours = dto.sleepTimeSeconds ? (dto.sleepTimeSeconds / 3600).toFixed(1) : '-'
      result.push({
        id: `sleep-${dto.sleepStartTimestampGMT}`,
        title: `${episode.kind === 'nap' ? '昼寝' : '睡眠'} ${durationHours}時間`,
        start: sleepStart,
        end: sleepEnd,
        color: '#8b5cf6',
        column: 2,
        icon: '🌙',
      })
    })

    // Column 2: 読書
    readHistory.forEach((h) => {
      const start = new Date(h.started_at)
      if (!isSameDate(start, selectedDate)) return
      const end = new Date(h.ended_at)
      result.push({
        id: `read-${h.id}`,
        title: h.product_title,
        subtitle: h.episode_title ?? undefined,
        start,
        end: end < start ? new Date(start.getTime() + 30 * 60 * 1000) : end,
        color: '#059669',
        column: 2,
        icon: '📖',
      })
    })

    // Column 2: Garmin アクティビティ
    activities.forEach((a) => {
      if (!a.start_time) return
      const start = new Date(a.start_time)
      if (!isSameDate(start, selectedDate)) return
      const end = new Date(start.getTime() + (a.duration_seconds ?? 0) * 1000)
      result.push({
        id: `garmin-${a.activity_id}`,
        title: a.activity_name,
        subtitle: a.duration_seconds ? `${Math.round(a.duration_seconds / 60)}分` : undefined,
        start,
        end,
        color: '#e11d48',
        column: 2,
        icon: '💪',
      })
    })

    return result.map((b) => ({ ...b, start: clamp(b.start), end: clamp(b.end), realStart: b.start, realEnd: b.end }))
  }, [
    calendarEvents,
    todos,
    workoutSessions,
    visits,
    storeMovementSessions,
    sleep,
    readHistory,
    activities,
    sleepWindows,
    selectedDate,
    parked.parkedIds,
  ])

  // 同一カラム内の重なるブロックのレイアウト情報を計算
  // 今日やってみる TODO の提案も TODO 列のブロックとして並べ、既存のタスクの下に隠れないようにする
  const blockLayout = useMemo(
    () =>
      computeOverlapLayout([
        ...blocks,
        ...(todayOverlay?.picks ?? []).map((p) => ({ id: p.id, column: 1, start: p.start, end: p.end })),
      ]),
    [blocks, todayOverlay],
  )

  // 締切のある TODO（選択日が締切日のもの）
  const deadlineTodos = useMemo(
    () => todos.filter((t) => isDeadlineTodo(t) && isSameDate(new Date(ensureUtc(t.deadline as string)), selectedDate)),
    [todos, selectedDate],
  )

  const handleBlockClick = (block: TimeBlock) => {
    const found = findItemTodo(todos, block.todoId)
    if (found) onTodoClick?.(found)
  }

  /** Y座標(列内)を15分刻みの分数に変換 */
  const yToSnappedMinutes = (y: number, containerHeight: number) => {
    const raw = Math.floor((y / containerHeight) * TOTAL_HOURS * 60)
    return Math.max(0, Math.min(Math.floor(raw / 15) * 15, TOTAL_HOURS * 60 - 15))
  }

  /** ドラッグ開始 */
  const handleColumnMouseDown = (e: React.MouseEvent<HTMLDivElement>, column: number) => {
    if (column !== 0 && column !== 1) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    dragRef.current = { startY: y, column }
    const totalHeight = TOTAL_HOURS * HOUR_HEIGHT
    const snapped = yToSnappedMinutes(y, totalHeight)
    const top = (snapped / (TOTAL_HOURS * 60)) * totalHeight
    setDragPreview({ top, height: (15 / (TOTAL_HOURS * 60)) * totalHeight, column })
  }

  /** ドラッグ中 */
  const handleColumnMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const totalHeight = TOTAL_HOURS * HOUR_HEIGHT
    const startMin = yToSnappedMinutes(dragRef.current.startY, totalHeight)
    const curMin = yToSnappedMinutes(y, totalHeight)
    const minStart = Math.min(startMin, curMin)
    const minEnd = Math.max(startMin, curMin) + 15
    const top = (minStart / (TOTAL_HOURS * 60)) * totalHeight
    const height = ((minEnd - minStart) / (TOTAL_HOURS * 60)) * totalHeight
    setDragPreview({ top, height, column: dragRef.current.column })
  }

  /** ドラッグ終了 → ダイアログを開く */
  const handleColumnMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current) return
    const { column } = dragRef.current
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const totalHeight = TOTAL_HOURS * HOUR_HEIGHT
    const startMin = yToSnappedMinutes(dragRef.current.startY, totalHeight)
    const curMin = yToSnappedMinutes(y, totalHeight)
    const minStart = Math.min(startMin, curMin)
    let minEnd = Math.max(startMin, curMin) + 15
    // クリック（ドラッグなし）の場合は30分スロット
    if (minEnd - minStart < 30) minEnd = minStart + 30

    const startDate = new Date(selectedDate)
    startDate.setHours(Math.floor(minStart / 60), minStart % 60, 0, 0)
    const endDate = new Date(selectedDate)
    endDate.setHours(Math.floor(minEnd / 60), minEnd % 60, 0, 0)

    openSlot(
      startDate,
      endDate,
      column === 0
        ? { defaultMode: 'event', disabledModes: ['new', 'existing'] }
        : { defaultMode: 'new', disabledModes: ['event'] },
    )
    dragRef.current = null
    setDragPreview(null)
  }

  /** ブロックの時間更新を実行する共通ヘルパー */
  const commitTimeChange = async (todoId: number, newStartMin: number, newEndMin: number) => {
    justDraggedRef.current = true
    setTimeout(() => {
      justDraggedRef.current = false
    }, 0)

    const startDate = new Date(selectedDate)
    startDate.setHours(Math.floor(newStartMin / 60), newStartMin % 60, 0, 0)
    const endDate = new Date(selectedDate)
    endDate.setHours(Math.floor(newEndMin / 60), newEndMin % 60, 0, 0)
    await rescheduleTodo(todoId, startDate, endDate)
  }

  /** ブロックのドラッグ開始 */
  const handleBlockMouseDown = (e: React.MouseEvent, block: TimeBlock) => {
    const { todoId } = block
    if (!todoId || !block.editable) return
    e.preventDefault()
    e.stopPropagation()
    const totalHeight = TOTAL_HOURS * HOUR_HEIGHT
    const startMin = block.start.getHours() * 60 + block.start.getMinutes()
    const originalTop = (startMin / (TOTAL_HOURS * 60)) * totalHeight
    blockDragRef.current = { block, startY: e.clientY, originalTop, containerHeight: totalHeight }
    setBlockDragState({ blockId: block.id, offsetTop: 0 })
    trayRef.current?.setAttribute('data-dragging', 'true')

    // トレイはグリッドの外にありグリッドの mouseup が来ないため、document で拾う
    const onMouseUp = (ev: MouseEvent) => {
      document.removeEventListener('mouseup', onMouseUp, true)
      trayRef.current?.removeAttribute('data-dragging')
      const isOnTray = isPointerInside(trayRef.current, ev)
      if (!isOnTray && gridRef.current?.contains(ev.target as Node)) return
      blockDragRef.current = null
      setBlockDragState(null)
      if (isOnTray) parked.park(todoId)
    }
    document.addEventListener('mouseup', onMouseUp, true)
  }

  /** 保留トレイからグリッドへのドロップ */
  const handleParkedDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const todoId = Number(e.dataTransfer.getData(PARKED_TODO_MIME))
    if (!todoId) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const minutes = yToSnappedMinutes(e.clientY - rect.top, rect.height)
    const start = new Date(selectedDate)
    start.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
    void parked.place(todoId, start)
  }

  /** リサイズハンドルのドラッグ開始 */
  const handleResizeMouseDown = (e: React.MouseEvent, block: TimeBlock, edge: 'top' | 'bottom') => {
    if (!block.todoId) return
    const todo = todos.find((t) => t.id === block.todoId)
    if (!todo || todo.status === 'done') return
    e.preventDefault()
    e.stopPropagation()
    const totalHeight = TOTAL_HOURS * HOUR_HEIGHT
    const originalStartMin = block.start.getHours() * 60 + block.start.getMinutes()
    const originalEndMin = block.end.getHours() * 60 + block.end.getMinutes()
    resizeDragRef.current = {
      block,
      edge,
      startY: e.clientY,
      containerHeight: totalHeight,
      originalStartMin,
      originalEndMin,
    }
    setResizeState({ blockId: block.id, deltaStartMin: 0, deltaEndMin: 0 })
  }

  /** ブロックドラッグ中（グリッド全体で追跡） */
  const handleGridMouseMove = (e: React.MouseEvent) => {
    // リサイズDnD（15分スナップ）
    if (resizeDragRef.current) {
      const { edge, startY, containerHeight, originalStartMin, originalEndMin } = resizeDragRef.current
      const deltaY = e.clientY - startY
      const deltaMin = Math.round(((deltaY / containerHeight) * TOTAL_HOURS * 60) / 15) * 15
      if (edge === 'top') {
        const newStart = Math.max(0, Math.min(originalStartMin + deltaMin, originalEndMin - 15))
        setResizeState({
          blockId: resizeDragRef.current.block.id,
          deltaStartMin: newStart - originalStartMin,
          deltaEndMin: 0,
        })
      } else {
        const newEnd = Math.max(originalStartMin + 15, Math.min(originalEndMin + deltaMin, TOTAL_HOURS * 60))
        setResizeState({
          blockId: resizeDragRef.current.block.id,
          deltaStartMin: 0,
          deltaEndMin: newEnd - originalEndMin,
        })
      }
      return
    }
    // ブロック移動DnD（15分スナップ）
    if (blockDragRef.current) {
      const { containerHeight } = blockDragRef.current
      const deltaY = e.clientY - blockDragRef.current.startY
      // ピクセルを分に変換し、15分単位にスナップしてからピクセルに戻す
      const deltaMin = (deltaY / containerHeight) * TOTAL_HOURS * 60
      const snappedMin = Math.round(deltaMin / 15) * 15
      const snappedY = (snappedMin / (TOTAL_HOURS * 60)) * containerHeight
      setBlockDragState({ blockId: blockDragRef.current.block.id, offsetTop: snappedY })
      return
    }
    // 既存のドラッグ選択処理をフォールスルー
  }

  /** ブロックドラッグ終了 */
  const handleGridMouseUp = async (e: React.MouseEvent) => {
    // リサイズ完了処理
    if (resizeDragRef.current) {
      const { block, edge, startY, containerHeight, originalStartMin, originalEndMin } = resizeDragRef.current
      const deltaY = e.clientY - startY
      const deltaMin = Math.round(((deltaY / containerHeight) * TOTAL_HOURS * 60) / 15) * 15
      resizeDragRef.current = null
      setResizeState(null)

      let newStartMin = originalStartMin
      let newEndMin = originalEndMin
      if (edge === 'top') {
        newStartMin = Math.max(0, Math.min(originalStartMin + deltaMin, originalEndMin - 15))
      } else {
        newEndMin = Math.max(originalStartMin + 15, Math.min(originalEndMin + deltaMin, TOTAL_HOURS * 60))
      }
      if (newStartMin === originalStartMin && newEndMin === originalEndMin) return

      await commitTimeChange(block.todoId!, newStartMin, newEndMin)
      return
    }

    if (!blockDragRef.current) return
    const { block, startY, containerHeight } = blockDragRef.current
    const deltaY = e.clientY - startY
    blockDragRef.current = null
    setBlockDragState(null)

    // 移動量を15分スナップで判定
    const deltaMin = (deltaY / containerHeight) * TOTAL_HOURS * 60
    const snappedMin = Math.round(deltaMin / 15) * 15
    if (snappedMin === 0) return

    // 新しい開始時刻を計算（15分スナップ）
    const durationMin =
      block.end.getHours() * 60 + block.end.getMinutes() - (block.start.getHours() * 60 + block.start.getMinutes())
    const originalStartMin = block.start.getHours() * 60 + block.start.getMinutes()
    const newStartMin = Math.max(0, Math.min(originalStartMin + snappedMin, TOTAL_HOURS * 60 - durationMin))
    const newEndMin = newStartMin + durationMin

    await commitTimeChange(block.todoId!, newStartMin, newEndMin)
  }

  // 現在時刻インジケーターの位置（%）— 1分ごとに更新
  const [nowPosition, setNowPosition] = useState<number | null>(() => {
    if (!isToday) return null
    const now = new Date()
    return ((now.getHours() * 60 + now.getMinutes()) / (TOTAL_HOURS * 60)) * 100
  })

  useEffect(() => {
    if (!isToday) {
      setNowPosition(null)
      return
    }
    const update = () => {
      const now = new Date()
      setNowPosition(((now.getHours() * 60 + now.getMinutes()) / (TOTAL_HOURS * 60)) * 100)
    }
    update()
    const id = setInterval(update, 60_000)
    return () => clearInterval(id)
  }, [isToday])

  // 表示したら現在時刻が見える位置までスクロール
  useEffect(() => {
    if (gridRef.current) scrollToNow(gridRef.current)
  }, [])

  const toolbarTitle = selectedDate.toLocaleDateString('ja', { year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <>
      <div className="flex flex-col flex-1 min-h-0 overflow-clip">
        <div className="sticky top-0 z-40 bg-background">
          <CalendarToolbar
            title={toolbarTitle}
            activeView="day"
            onPrev={goPrev}
            onNext={goNext}
            onToday={goToday}
            onViewChange={(v) => {
              if (v !== 'day') onViewChange?.(v)
            }}
            onAutoSchedule={onAutoSchedule}
            isScheduling={isScheduling}
          />

          {/* 列ヘッダー */}
          <div className="grid grid-cols-[48px_1fr_1fr_1fr] border-b bg-background">
            <div />
            {COLUMN_LABELS.map((label) => (
              <div key={label} className="text-xs font-medium text-muted-foreground text-center py-1.5 border-l">
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* 締切 TODO 行 */}
        {deadlineTodos.length > 0 && (
          <div className="grid grid-cols-[48px_1fr] border-b bg-background">
            <div className="flex items-center justify-end pr-2">
              <span className="text-[0.65rem] text-muted-foreground">締切</span>
            </div>
            <div className="flex flex-wrap gap-1 px-2 py-1.5">
              {deadlineTodos.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-white transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#d97706' }}
                  onClick={() => onTodoClick?.(t)}
                >
                  <span>🔔</span>
                  <span className="truncate max-w-35">{t.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {isToday && hasFullAccess && (
          <TodayAdviceRow today={todayOverlay ? today : null} generating={isTodayGenerating} />
        )}

        {/* タイムグリッド */}
        <div
          ref={gridRef}
          className="relative grid grid-cols-[48px_1fr_1fr_1fr]"
          style={{ height: TOTAL_HOURS * HOUR_HEIGHT }}
          onMouseMove={handleGridMouseMove}
          onMouseUp={handleGridMouseUp}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes(PARKED_TODO_MIME)) e.preventDefault()
          }}
          onDrop={handleParkedDrop}
          onMouseLeave={(e) => {
            // 保留トレイへの移動中はドラッグを続ける
            if (e.relatedTarget instanceof Node && trayRef.current?.contains(e.relatedTarget)) return
            if (blockDragRef.current) {
              blockDragRef.current = null
              setBlockDragState(null)
            }
            if (resizeDragRef.current) {
              resizeDragRef.current = null
              setResizeState(null)
            }
          }}
        >
          {/* 時間軸ラベル + 横線 */}
          {Array.from({ length: TOTAL_HOURS }, (_, h) => (
            <div
              key={h}
              className="col-start-1 col-end-5 border-t border-border/40"
              style={{ position: 'absolute', top: h * HOUR_HEIGHT, width: '100%', height: HOUR_HEIGHT }}
            >
              <span className="text-[0.65rem] text-muted-foreground/70 pr-2 inline-block w-12 text-right -translate-y-1/2">
                {String(h).padStart(2, '0')}
              </span>
            </div>
          ))}

          {/* 列区切り線 */}
          <div className="absolute top-0 bottom-0 border-l border-border/30" style={{ left: '48px' }} />
          <div
            className="absolute top-0 bottom-0 border-l border-border/30"
            style={{ left: 'calc(48px + (100% - 48px) / 3)' }}
          />
          <div
            className="absolute top-0 bottom-0 border-l border-border/30"
            style={{ left: 'calc(48px + (100% - 48px) * 2 / 3)' }}
          />

          {/* 予定列（Column 0）ドラッグエリア */}
          <div
            className="absolute top-0 bottom-0 z-5 cursor-crosshair hover:bg-orange-500/5 transition-colors"
            style={{ left: '48px', width: 'calc((100% - 48px) / 3)' }}
            onMouseDown={(e) => handleColumnMouseDown(e, 0)}
            onMouseMove={handleColumnMouseMove}
            onMouseUp={handleColumnMouseUp}
            onMouseLeave={() => {
              if (dragRef.current) {
                dragRef.current = null
                setDragPreview(null)
              }
            }}
          />
          {/* TODO列（Column 1）ドラッグエリア */}
          <div
            className="absolute top-0 bottom-0 z-5 cursor-crosshair hover:bg-blue-500/5 transition-colors"
            style={{ left: 'calc(48px + (100% - 48px) / 3)', width: 'calc((100% - 48px) / 3)' }}
            onMouseDown={(e) => handleColumnMouseDown(e, 1)}
            onMouseMove={handleColumnMouseMove}
            onMouseUp={handleColumnMouseUp}
            onMouseLeave={() => {
              if (dragRef.current) {
                dragRef.current = null
                setDragPreview(null)
              }
            }}
          />

          {/* ドラッグ選択プレビュー */}
          {dragPreview && (
            <div
              className="absolute rounded border-2 border-primary bg-primary/20 pointer-events-none z-20"
              style={{
                top: dragPreview.top,
                height: Math.max(dragPreview.height, 8),
                left: `calc(48px + (100% - 48px) * ${dragPreview.column} / 3 + 2px)`,
                width: `calc${columnInnerWidth(dragPreview.column, slotLaneWidth)}`,
              }}
            />
          )}

          {/* 現在時刻インジケーター */}
          {nowPosition !== null && (
            <div className="absolute left-0 right-0 z-30 pointer-events-none" style={{ top: `${nowPosition}%` }}>
              <div className="h-0.5 bg-red-500 w-full" />
            </div>
          )}

          {todayOverlay && (
            <TodaySuggestionLayer
              overlay={todayOverlay}
              hourHeight={HOUR_HEIGHT}
              todos={todos}
              onTodoClick={onTodoClick}
              layout={blockLayout}
              laneWidth={slotLaneWidth}
            />
          )}

          {/* イベントブロック */}
          {blocks.map((block) => {
            const startMin = block.start.getHours() * 60 + block.start.getMinutes()
            const endMin = block.end.getHours() * 60 + block.end.getMinutes()
            const isResizing = resizeState?.blockId === block.id
            const effectiveStartMin = isResizing ? startMin + resizeState.deltaStartMin : startMin
            const effectiveEndMin = isResizing ? endMin + resizeState.deltaEndMin : endMin
            const durationMin = Math.max(effectiveEndMin - effectiveStartMin, 15)
            const top = (effectiveStartMin / (TOTAL_HOURS * 60)) * TOTAL_HOURS * HOUR_HEIGHT
            const height = (durationMin / (TOTAL_HOURS * 60)) * TOTAL_HOURS * HOUR_HEIGHT
            const isDragging = blockDragState?.blockId === block.id
            const dragOffset = isDragging ? blockDragState.offsetTop : 0
            const isDraggable = !!block.editable

            // 重なりレイアウト
            const overlap = blockLayout.get(block.id) ?? { subCol: 0, totalCols: 1 }
            const overlapSlot = block.column === 1 ? findOverlappingSlot(todayOverlay, block.start, block.end) : null
            const slotBorder = overlapSlot
              ? `border-l-[3px] pr-4 ${SLOT_STYLE.get(overlapSlot.slot.kind)?.border ?? ''}`
              : ''

            // 時間ラベル（ドラッグ中は移動先、リサイズ中は新しい範囲、通常時は現在の時間帯を表示。15分以下は非表示）
            let timeLabel: string | null = null
            if (isResizing) {
              timeLabel = `${fmtTime(effectiveStartMin)}–${fmtTime(effectiveEndMin)}`
            } else if (isDragging && dragOffset !== 0) {
              const origDuration = endMin - startMin
              const dragMinDelta = (dragOffset / (TOTAL_HOURS * HOUR_HEIGHT)) * TOTAL_HOURS * 60
              const newStart = Math.max(
                0,
                Math.min(startMin + Math.round(dragMinDelta / 15) * 15, TOTAL_HOURS * 60 - origDuration),
              )
              const newEnd = newStart + origDuration
              timeLabel = `${fmtTime(newStart)}–${fmtTime(newEnd)}`
            } else if (durationMin > 15) {
              const { realStart, realEnd } = block
              if (realStart && realEnd && isCrossDay(realStart, realEnd)) {
                const range = formatCrossDayRange(realStart, realEnd, selectedDate, fmtDate)
                timeLabel = `${range.start}–${range.end}`
              } else {
                timeLabel = `${fmtTime(startMin)}–${fmtTime(endMin)}`
              }
            }

            return (
              <div
                key={block.id}
                className={`absolute rounded px-1.5 py-0.5 text-white text-xs overflow-hidden hover:opacity-90 ${
                  isDraggable ? 'cursor-grab active:cursor-grabbing' : block.todoId ? 'cursor-pointer' : ''
                } ${isDragging || isResizing ? 'opacity-80 shadow-lg ring-2 ring-primary/50' : ''} ${slotBorder}`}
                style={{
                  top: top + dragOffset,
                  height: Math.max(height, 16),
                  backgroundColor: block.color,
                  ...blockHorizontalStyle(block.column, slotLaneWidth, overlap),
                  zIndex: isDragging || isResizing ? 50 : 10,
                  transition: isDragging || isResizing ? 'none' : 'opacity 0.15s',
                  userSelect: 'none',
                }}
                onMouseDown={(e) => (isDraggable ? handleBlockMouseDown(e, block) : undefined)}
                onClick={() => !justDraggedRef.current && handleBlockClick(block)}
              >
                {/* 上端リサイズハンドル */}
                {isDraggable && (
                  <div
                    className="absolute top-0 left-0 right-0 h-2 cursor-n-resize z-10"
                    onMouseDown={(e) => handleResizeMouseDown(e, block, 'top')}
                  />
                )}
                {overlapSlot && <SlotBadge item={overlapSlot} />}
                {timeLabel && <div className="text-[10px] font-bold opacity-90 leading-tight">{timeLabel}</div>}
                <div className="font-medium truncate leading-tight">
                  {block.icon && <span className="mr-0.5">{block.icon}</span>}
                  {block.title}
                </div>
                {block.subtitle && height > 24 && (
                  <div className="text-[10px] opacity-80 truncate">{block.subtitle}</div>
                )}
                {/* 下端リサイズハンドル */}
                {isDraggable && (
                  <div
                    className="absolute bottom-0 left-0 right-0 h-2 cursor-s-resize z-10"
                    onMouseDown={(e) => handleResizeMouseDown(e, block, 'bottom')}
                  />
                )}
              </div>
            )
          })}
        </div>
        <ParkingTray trayRef={trayRef} todos={parked.parkedTodos} onRemove={parked.unpark} dragMode="native" />
      </div>
      {slotDialogProps && <SlotCreateDialog {...slotDialogProps} />}
    </>
  )
}
